import axios from "axios";
import type { AxiosRequestConfig, AxiosResponse } from "axios";
import omit from "lodash/omit";

import { headers, host } from "./shared";
import type {
  Project,
  ProjectContext,
  ProjectError,
  ProjectErrorRecord,
  ProjectFeedback,
  ProjectFile,
  ProjectListItem,
  ProjectRemix,
  SchoolProjectStatus,
} from "../../types/project";

type AccessToken = string | null | undefined;

export const createOrUpdateProject = async (
  projectWithUserId: Project,
  accessToken: AccessToken,
): Promise<AxiosResponse<Project>> => {
  const project = omit(projectWithUserId, ["user_id"]);
  if (!project.identifier) {
    return await axios.post(
      `${host}/api/projects`,
      { project },
      headers(accessToken),
    );
  } else {
    return await axios.put(
      `${host}/api/projects/${project.identifier}`,
      { project },
      headers(accessToken),
    );
  }
};

export const deleteProject = async (
  identifier: string,
  accessToken: AccessToken,
): Promise<AxiosResponse<void>> => {
  return await axios.delete(
    `${host}/api/projects/${identifier}`,
    headers(accessToken),
  );
};

export const getImage = async (
  url: string,
): Promise<AxiosResponse<unknown>> => {
  return await axios.get(url, headers());
};

export const loadRemix = async (
  projectIdentifier: string,
  accessToken: AccessToken,
): Promise<AxiosResponse<Project>> => {
  return await axios.get(
    `${host}/api/projects/${projectIdentifier}/remix`,
    headers(accessToken),
  );
};

export const getRemixIdentifier = async (
  projectIdentifier: string,
  accessToken: AccessToken,
): Promise<AxiosResponse<{ identifier: string }>> => {
  return await axios.get(
    `${host}/api/projects/${projectIdentifier}/remix/identifier`,
    headers(accessToken),
  );
};

export const getRemixes = async (
  projectIdentifier: string,
  accessToken: AccessToken,
): Promise<AxiosResponse<ProjectRemix[]>> => {
  return await axios.get(
    `${host}/api/projects/${projectIdentifier}/remixes`,
    headers(accessToken),
  );
};

export const createRemix = async (
  project: Project,
  accessToken: AccessToken,
): Promise<AxiosResponse<Project>> => {
  return await axios.post(
    `${host}/api/projects/${project.identifier}/remix`,
    { project },
    headers(accessToken),
  );
};

export const readProject = async (
  projectIdentifier: string,
  locale: string | null | undefined,
  accessToken: AccessToken,
): Promise<AxiosResponse<Project>> => {
  const queryString = locale ? `?locale=${locale}` : "";
  return await axios.get(
    `${host}/api/projects/${projectIdentifier}${queryString}`,
    headers(accessToken),
  );
};

export const readProjectList = async (
  page: number,
  accessToken: AccessToken,
): Promise<AxiosResponse<ProjectListItem[]>> => {
  return await axios.get(`${host}/api/projects`, {
    params: { page },
    ...headers(accessToken),
  });
};

export const getProjectContext = async (
  identifier: string,
  accessToken: AccessToken,
): Promise<AxiosResponse<ProjectContext>> => {
  return await axios.get(
    `${host}/api/projects/${identifier}/context`,
    headers(accessToken),
  );
};

export const uploadImages = async (
  projectIdentifier: string,
  accessToken: AccessToken,
  images: File[],
): Promise<AxiosResponse<{ image_list: ProjectFile[] }>> => {
  var formData = new FormData();

  images.forEach((image) => {
    formData.append("images[]", image, image.name);
  });

  return await axios.post(
    `${host}/api/projects/${projectIdentifier}/images`,
    formData,
    {
      ...headers(accessToken),
      "Content-Type": "multipart/form-data",
    } as AxiosRequestConfig,
  );
};

export const createError = async (
  projectIdentifier: string,
  userId: string | null | undefined,
  error: ProjectError,
  sendError: boolean = false,
): Promise<AxiosResponse<{ data: ProjectErrorRecord }> | undefined> => {
  if (!sendError) {
    return;
  }
  const { errorMessage, errorType } = error;
  return await axios.post(
    `${host}/api/project_errors`,
    {
      error: errorMessage,
      error_type: errorType,
      project_id: projectIdentifier,
      user_id: userId,
    },
    undefined,
  );
};

export const createProjectFeedback = async (
  projectIdentifier: string,
  accessToken: AccessToken,
  content: string,
): Promise<AxiosResponse<ProjectFeedback>> => {
  return await axios.post(
    `${host}/api/projects/${projectIdentifier}/feedback`,
    { content },
    headers(accessToken),
  );
};

export const readProjectFeedbackList = async (
  projectIdentifier: string,
  accessToken: AccessToken,
): Promise<AxiosResponse<ProjectFeedback[]>> => {
  return await axios.get(
    `${host}/api/projects/${projectIdentifier}/feedback`,
    headers(accessToken),
  );
};

export const markProjectFeedbackAsRead = async (
  projectIdentifier: string,
  accessToken: AccessToken,
  feedbackId: string,
): Promise<AxiosResponse<ProjectFeedback>> => {
  return await axios.put(
    `${host}/api/projects/${projectIdentifier}/feedback/${feedbackId}/read`,
    {},
    headers(accessToken),
  );
};

export const deleteProjectFeedback = async (
  projectIdentifier: string,
  accessToken: AccessToken,
  feedbackId: string,
): Promise<AxiosResponse<void>> => {
  return await axios.delete(
    `${host}/api/projects/${projectIdentifier}/feedback/${feedbackId}`,
    headers(accessToken),
  );
};

export const getProjectStatus = async (
  projectIdentifier: string,
  accessToken: AccessToken,
): Promise<AxiosResponse<SchoolProjectStatus>> => {
  return await axios.get(
    `${host}/api/projects/${projectIdentifier}/status`,
    headers(accessToken),
  );
};

export const setProjectStatusUnsubmitted = async (
  projectIdentifier: string,
  accessToken: AccessToken,
): Promise<AxiosResponse<SchoolProjectStatus>> => {
  return await axios.post(
    `${host}/api/projects/${projectIdentifier}/unsubmit`,
    {},
    headers(accessToken),
  );
};

export const setProjectStatusSubmitted = async (
  projectIdentifier: string,
  accessToken: AccessToken,
): Promise<AxiosResponse<SchoolProjectStatus>> => {
  return await axios.post(
    `${host}/api/projects/${projectIdentifier}/submit`,
    {},
    headers(accessToken),
  );
};

export const setProjectStatusReturned = async (
  projectIdentifier: string,
  accessToken: AccessToken,
): Promise<AxiosResponse<SchoolProjectStatus>> => {
  return await axios.post(
    `${host}/api/projects/${projectIdentifier}/return`,
    {},
    headers(accessToken),
  );
};

export const setProjectStatusComplete = async (
  projectIdentifier: string,
  accessToken: AccessToken,
): Promise<AxiosResponse<SchoolProjectStatus>> => {
  return await axios.post(
    `${host}/api/projects/${projectIdentifier}/complete`,
    {},
    headers(accessToken),
  );
};
